/**
 * 점검항목(inspectList) 마이그레이션 유틸리티
 * - 템플릿 변경 시 기존 저장 데이터의 체크 상태를 유지하면서 최신 구조로 마이그레이션
 * - 카테고리 간 항목 이동도 지원
 *
 * @example
 * import { createInspectListMigrator } from '@/util/InspectListMigration';
 *
 * // 컴포넌트에서 사용
 * data() {
 *   return {
 *     migrator: createInspectListMigrator(inspectData, [
 *       { from: { categoryId: 2, itemId: 2 }, to: { categoryId: 5, itemId: 5 } }
 *     ])
 *   };
 * }
 *
 * // value watcher에서
 * if (this.migrator.needsMigration(savedList)) {
 *   this.form.inspectList = this.migrator.migrate(savedList);
 * }
 */

/**
 * 마이그레이션 유틸리티 생성
 * @param {Array} template - inspectData 템플릿
 * @param {Array} crossCategoryMapping - 카테고리 간 이동 매핑 (선택)
 * @returns {Object} 마이그레이션 유틸리티 객체
 */
export function createInspectListMigrator(template, crossCategoryMapping) {
  crossCategoryMapping = crossCategoryMapping || [];

  /**
   * 마이그레이션이 필요한지 확인
   * @param {Array} savedList 저장된 inspectList 데이터
   * @returns {boolean} 마이그레이션 필요 여부
   */
  function needsMigration(savedList) {
    if (!savedList || savedList.length === 0) return true;

    // 카테고리 간 이동 매핑이 있으면 체크
    if (crossCategoryMapping.length > 0) {
      for (var m = 0; m < crossCategoryMapping.length; m++) {
        var mapping = crossCategoryMapping[m];
        var savedCategory = savedList.find(function(item) { return item.id === mapping.from.categoryId; });
        if (savedCategory && savedCategory.list) {
          var savedItem = savedCategory.list.find(function(item) { return item.id === mapping.from.itemId; });
          if (savedItem && (savedItem.is_checked || savedItem.note)) {
            return true;
          }
        }
      }
    }

    // 항목 개수가 다르면 마이그레이션 필요
    if (savedList.length !== template.length) return true;

    // ID 순서나 label이 다른지 확인
    for (var i = 0; i < template.length; i++) {
      var templateItem = template[i];
      var savedItem = savedList[i];

      if (templateItem.id !== savedItem.id) return true;
      if (templateItem.label !== savedItem.label) return true;

      // 내부 list 항목도 확인
      if (templateItem.list && savedItem.list) {
        if (templateItem.list.length !== savedItem.list.length) return true;

        for (var j = 0; j < templateItem.list.length; j++) {
          var savedSubItem = savedItem.list[j];
          if (!savedSubItem) return true;
          if (templateItem.list[j].id !== savedSubItem.id) return true;
          if (templateItem.list[j].label !== savedSubItem.label) return true;
        }
      }
    }

    return false;
  }

  /**
   * 카테고리 간 이동 매핑에서 기존 데이터 추출
   * @param {Array} savedList 저장된 inspectList 데이터
   * @returns {Object} to 위치를 key로, 기존 데이터를 value로 하는 객체
   */
  function extractCrossCategoryData(savedList) {
    var dataMap = {};

    for (var m = 0; m < crossCategoryMapping.length; m++) {
      var mapping = crossCategoryMapping[m];
      var savedCategory = savedList.find(function(item) { return item.id === mapping.from.categoryId; });
      if (savedCategory && savedCategory.list) {
        var savedItem = savedCategory.list.find(function(item) { return item.id === mapping.from.itemId; });
        if (savedItem) {
          var key = mapping.to.categoryId + '_' + mapping.to.itemId;
          dataMap[key] = {
            is_checked: savedItem.is_checked || "",
            note: savedItem.note || ""
          };
        }
      }
    }

    return dataMap;
  }

  /**
   * 내부 list 항목 마이그레이션
   * @param {number} categoryId 현재 카테고리 ID
   * @param {Array} templateList 템플릿 list
   * @param {Array} savedList 저장된 list
   * @param {Object} crossMappingData 카테고리 간 이동 매핑 데이터
   * @returns {Array} 체크 상태가 복원된 list
   */
  function migrateItemList(categoryId, templateList, savedList, crossMappingData) {
    if (!savedList) savedList = [];

    // 저장된 데이터를 ID 기준으로 객체로 변환
    var savedMap = {};
    for (var i = 0; i < savedList.length; i++) {
      savedMap[savedList[i].id] = savedList[i];
    }

    return templateList.map(function(templateItem) {
      // 먼저 카테고리 간 이동 매핑 확인
      var crossKey = categoryId + '_' + templateItem.id;
      var crossData = crossMappingData ? crossMappingData[crossKey] : null;

      if (crossData) {
        return Object.assign({}, templateItem, {
          is_checked: crossData.is_checked,
          note: crossData.note
        });
      }

      // 같은 카테고리 내 매칭
      var savedItem = savedMap[templateItem.id];
      if (savedItem) {
        return Object.assign({}, templateItem, {
          is_checked: savedItem.is_checked || "",
          note: savedItem.note || ""
        });
      }

      return templateItem;
    });
  }

  /**
   * 저장된 데이터를 최신 템플릿 구조로 마이그레이션
   * @param {Array} savedList 저장된 inspectList 데이터
   * @returns {Array} 최신 템플릿 구조에 체크 상태가 복원된 inspectList
   */
  function migrate(savedList) {
    // 최신 템플릿 복사
    var result = JSON.parse(JSON.stringify(template));

    if (!savedList || savedList.length === 0) {
      return result;
    }

    // 저장된 데이터를 ID 기준으로 객체로 변환
    var savedMap = {};
    for (var i = 0; i < savedList.length; i++) {
      savedMap[savedList[i].id] = savedList[i];
    }

    // 카테고리 간 이동 매핑 데이터 추출
    var crossMappingData = extractCrossCategoryData(savedList);

    // 템플릿 기준으로 순회하며 저장된 체크 상태 복원
    for (var j = 0; j < result.length; j++) {
      var templateItem = result[j];
      var savedItem = savedMap[templateItem.id];

      if (savedItem) {
        templateItem.check = savedItem.check !== undefined ? savedItem.check : false;
        templateItem.list = migrateItemList(templateItem.id, templateItem.list, savedItem.list, crossMappingData);
      }
    }

    return result;
  }

  /**
   * 빈 템플릿 생성
   * @returns {Array} 초기화된 inspectList
   */
  function createEmpty() {
    return JSON.parse(JSON.stringify(template));
  }

  return {
    needsMigration: needsMigration,
    migrate: migrate,
    createEmpty: createEmpty
  };
}

/**
 * flat 배열 구조의 점검항목 마이그레이션 유틸리티
 * - 1090처럼 카테고리 없이 { id, title, content, isChecked } 형태인 경우 사용
 * - 구버전(id 없는 저장 데이터)은 content 문자열로 매칭
 */
export function createFlatInspectListMigrator(template) {
  function needsMigration(savedList) {
    if (!savedList || savedList.length === 0) return true;
    if (!savedList[0].id) return true;
    if (savedList.length !== template.length) return true;
    const savedMap = {};
    savedList.forEach(item => { savedMap[item.id] = item; });
    return template.some(item => {
      const saved = savedMap[item.id];
      return !saved || saved.content !== item.content || saved.title !== item.title;
    });
  }

  function migrate(savedList) {
    if (!savedList || savedList.length === 0) {
      return JSON.parse(JSON.stringify(template));
    }
    if (!savedList[0].id) {
      const savedByContent = {};
      savedList.forEach(item => { savedByContent[item.content] = item; });
      return template.map(templateItem => Object.assign({}, templateItem, {
        isChecked: (savedByContent[templateItem.content] || {}).isChecked || "",
      }));
    }
    const savedMap = {};
    savedList.forEach(item => { savedMap[item.id] = item; });
    return template.map(templateItem => Object.assign({}, templateItem, {
      isChecked: (savedMap[templateItem.id] || {}).isChecked || "",
    }));
  }

  function createEmpty() {
    return JSON.parse(JSON.stringify(template));
  }

  return { needsMigration: needsMigration, migrate: migrate, createEmpty: createEmpty };
}

/**
 * 섹션 배열 구조의 점검항목 마이그레이션 유틸리티
 * - 613처럼 [{ title, list: [{ title, content, isChecked }] }] 형태인 경우 사용
 * - 섹션은 title로, 항목은 content로 매칭
 */
export function createSectionedInspectListMigrator(template) {
  function needsMigration(savedList) {
    if (!savedList || savedList.length === 0) return true;
    if (savedList.length !== template.length) return true;
    return template.some(function(templateSection, i) {
      const savedSection = savedList[i];
      if (!savedSection || savedSection.title !== templateSection.title) return true;
      if (!savedSection.list || savedSection.list.length !== templateSection.list.length) return true;
      const savedIds = new Set(savedSection.list.map(function(item) { return item.id; }));
      return templateSection.list.some(function(item) { return !savedIds.has(item.id); });
    });
  }

  function migrate(savedList) {
    const result = JSON.parse(JSON.stringify(template));
    if (!savedList || savedList.length === 0) return result;

    const savedByTitle = {};
    savedList.forEach(function(section) {
      savedByTitle[section.title] = section;
    });

    result.forEach(function(section) {
      const savedSection = savedByTitle[section.title];
      if (!savedSection || !savedSection.list) return;

      // id 우선 매칭, 없으면 content로 fallback (구버전 저장 데이터 호환)
      const savedById = {};
      const savedByContent = {};
      savedSection.list.forEach(function(item) {
        if (item.id !== undefined) savedById[item.id] = item;
        if (item.content) savedByContent[item.content] = item;
      });

      section.list.forEach(function(item) {
        const saved = savedById[item.id] || (item.content ? savedByContent[item.content] : null);
        if (!saved) return;
        item.isChecked = saved.isChecked || "";
        ["input1", "input2", "input3", "input4", "note"].forEach(function(key) {
          if (saved[key] !== undefined) item[key] = saved[key];
        });
      });
    });

    return result;
  }

  function createEmpty() {
    return JSON.parse(JSON.stringify(template));
  }

  return { needsMigration: needsMigration, migrate: migrate, createEmpty: createEmpty };
}

export default {
  createInspectListMigrator: createInspectListMigrator,
  createFlatInspectListMigrator: createFlatInspectListMigrator,
  createSectionedInspectListMigrator: createSectionedInspectListMigrator,
};
